import { MultiTypeAverageWindow } from '../utils/MultiTypeAverageWindow';
import {
  type EngagedRange,
  LayoutManager,
  type LayoutManagerOptions,
  type RVLayout,
  type RVLayoutInfo,
} from './LayoutManager';

const DEFAULT_ITEM_SIZE = 200;
const AVERAGE_WINDOW_CAPACITY = 10;

/**
 * Single-axis stack. Items accumulate along the scroll axis (y for vertical,
 * x for horizontal); the perpendicular axis is pinned to the bounded size.
 * `measuredSizes` stores the primary-axis dimension only — height when
 * vertical, width when horizontal.
 */
export class LinearLayoutManager extends LayoutManager {
  private readonly measuredSizes = new Map<number, number>();
  private readonly averageWindow: MultiTypeAverageWindow;
  private readonly defaultItemSize: number;

  constructor(options: LayoutManagerOptions) {
    super(options);
    this.defaultItemSize = options.defaultItemSize ?? DEFAULT_ITEM_SIZE;
    this.averageWindow = new MultiTypeAverageWindow(
      options.averageWindowCapacity ?? AVERAGE_WINDOW_CAPACITY,
      this.defaultItemSize,
    );
  }

  processLayoutInfo(infos: RVLayoutInfo[]): void {
    // Reject measurements before the scroller has a real viewport. ViewHolders
    // mounted with windowSize=0 take their content's natural width, which is
    // not what we want to record into the rolling average. The first valid
    // setWindowSize will trigger a render that re-measures at the right size.
    if (this.windowSize.width === 0 || this.windowSize.height === 0) return;
    for (const { index, dimensions } of infos) {
      if (index < 0 || index >= this.count) continue;
      const reported = this.horizontal ? dimensions.width : dimensions.height;
      // Drop zeros — they happen mid-mount when the cell hasn't laid out yet,
      // and feeding them in causes a self-perpetuating collapse (size=0 →
      // render at 0 → ResizeObserver reports 0 → loops).
      if (reported === 0) continue;
      const previous = this.measuredSizes.get(index);
      if (previous === reported) continue;
      this.measuredSizes.set(index, reported);
      this.averageWindow.addValue(reported, this.typeOf(index));
    }
  }

  getLayout(index: number): RVLayout {
    const size = this.sizeAt(index);
    const offset = this.offsetAt(index);
    if (this.horizontal) {
      return {
        x: offset,
        y: 0,
        width: size,
        height: this.windowSize.height,
        isMeasured: this.measuredSizes.has(index),
      };
    }
    return {
      x: 0,
      y: offset,
      width: this.windowSize.width,
      height: size,
      isMeasured: this.measuredSizes.has(index),
    };
  }

  getItemHeight(index: number): number {
    return this.horizontal ? this.windowSize.height : this.sizeAt(index);
  }

  getContentSize(): { width: number; height: number } {
    const total = this.offsetAt(this.count);
    return this.horizontal
      ? { width: total, height: this.windowSize.height }
      : { width: this.windowSize.width, height: total };
  }

  findIndicesInRange(
    rangeStart: number,
    rangeEnd: number,
  ): EngagedRange | null {
    if (this.count === 0) return null;
    let cursor = 0;
    let start = -1;
    let end = -1;
    for (let index = 0; index < this.count; index++) {
      const size = this.sizeAt(index);
      const itemStart = cursor;
      const itemEnd = cursor + size;
      if (itemEnd > rangeStart && itemStart < rangeEnd) {
        if (start === -1) start = index;
        end = index;
      } else if (itemStart >= rangeEnd) {
        break;
      }
      cursor = itemEnd;
    }
    if (start === -1) {
      // Range falls entirely past the content end — anchor on the last item
      // so callers (e.g. onEndReached) still have something to render.
      return { start: this.count - 1, end: this.count - 1 };
    }
    return { start, end };
  }

  shiftMeasurements(offset: number): void {
    if (offset === 0) return;
    const entries = Array.from(this.measuredSizes.entries());
    this.measuredSizes.clear();
    for (const [oldIndex, size] of entries) {
      const newIndex = oldIndex + offset;
      if (newIndex >= 0 && newIndex < this.count) {
        this.measuredSizes.set(newIndex, size);
      }
    }
  }

  protected override onItemCountChanged(): void {
    for (const index of [...this.measuredSizes.keys()]) {
      if (index >= this.count) this.measuredSizes.delete(index);
    }
  }

  private sizeAt(index: number): number {
    const measured = this.measuredSizes.get(index);
    if (measured != null) return measured;
    return this.averageWindow.getCurrentValue(this.typeOf(index));
  }

  private offsetAt(target: number): number {
    const upTo = Math.min(target, this.count);
    let cursor = 0;
    for (let index = 0; index < upTo; index++) {
      cursor += this.sizeAt(index);
    }
    return cursor;
  }
}
